import type {
  ClosingTime,
  ClosingTimeRequest,
} from "@luna-restaurant/school-api";

import {
  compareDateOnly,
  isValidDateOnly,
} from "@/utils/closingTimes/berlinDateOnly";

type DisplayFields = {
  key: string;
  startDate: string | null;
  endDate: string | null;
  editedBy: string | null;
  schoolClass: string | null;
  name: string | null;
};

type ClosingTimeDisplayRecord = DisplayFields & {
  kind: "closing-time";
  closingTime: ClosingTime;
};

type PendingCreateDisplayRecord = DisplayFields & {
  kind: "pending-create-request";
  request: ClosingTimeRequest;
};

type PendingChangeDisplayRecord = DisplayFields & {
  kind: "pending-change-request";
  request: ClosingTimeRequest;
  closingTime: ClosingTime;
};

export type CloseTimesDisplayRecord =
  | ClosingTimeDisplayRecord
  | PendingCreateDisplayRecord
  | PendingChangeDisplayRecord;

export type PendingRequestDisplayRecord =
  PendingCreateDisplayRecord | PendingChangeDisplayRecord;

type ClosingTimeRequestAnomalyReason =
  | "create-has-closing-time"
  | "change-missing-closing-time"
  | "closing-time-not-received"
  | "duplicate-request-for-closing-time";

export type ClosingTimeRequestAnomaly = {
  reason: ClosingTimeRequestAnomalyReason;
  requestId: number;
  requestIri: string;
  action: ClosingTimeRequest["action"];
  closingTimeIri: string | null;
};

export type CloseTimesDisplayModel = {
  records: CloseTimesDisplayRecord[];
  anomalies: ClosingTimeRequestAnomaly[];
};

const toRequestFields = (request: ClosingTimeRequest): DisplayFields => ({
  key: `closing-time-request-${request.id}`,
  startDate: request.startDate ?? null,
  endDate: request.endDate ?? null,
  editedBy: request.editedBy ?? null,
  schoolClass: request.schoolClass ?? null,
  name: request.name ?? null,
});

const getStableId = (record: CloseTimesDisplayRecord): number =>
  record.kind === "closing-time" ? record.closingTime.id : record.request.id;

const sortCloseTimesDisplayRecords = (
  records: CloseTimesDisplayRecord[],
): CloseTimesDisplayRecord[] =>
  [...records].sort((left, right) => {
    const leftStartDate = left.startDate ?? "";
    const rightStartDate = right.startDate ?? "";
    const leftIsValid = isValidDateOnly(leftStartDate);
    const rightIsValid = isValidDateOnly(rightStartDate);

    if (leftIsValid && rightIsValid) {
      const startDateDiff = compareDateOnly(leftStartDate, rightStartDate) ?? 0;
      if (startDateDiff !== 0) {
        return startDateDiff;
      }
    } else if (leftIsValid !== rightIsValid) {
      return leftIsValid ? -1 : 1;
    }

    return getStableId(left) - getStableId(right);
  });

export const buildCloseTimesDisplayModel = (
  closingTimes: ClosingTime[],
  closingTimeRequests: ClosingTimeRequest[],
): CloseTimesDisplayModel => {
  const closingTimeByIri = new Map(
    closingTimes.map((closingTime) => [closingTime["@id"], closingTime]),
  );
  const requestByClosingTimeIri = new Map<string, ClosingTimeRequest>();
  const createRequests: ClosingTimeRequest[] = [];
  const anomalies: ClosingTimeRequestAnomaly[] = [];

  const report = (
    request: ClosingTimeRequest,
    reason: ClosingTimeRequestAnomalyReason,
  ) => {
    anomalies.push({
      reason,
      requestId: request.id,
      requestIri: request["@id"],
      action: request.action,
      closingTimeIri: request.closingTime ?? null,
    });
  };

  for (const request of closingTimeRequests) {
    if (request.action === "CREATE") {
      if (request.closingTime) {
        report(request, "create-has-closing-time");
      } else {
        createRequests.push(request);
      }
      continue;
    }

    if (!request.closingTime) {
      report(request, "change-missing-closing-time");
      continue;
    }

    if (!closingTimeByIri.has(request.closingTime)) {
      report(request, "closing-time-not-received");
      continue;
    }

    if (requestByClosingTimeIri.has(request.closingTime)) {
      report(request, "duplicate-request-for-closing-time");
      continue;
    }

    requestByClosingTimeIri.set(request.closingTime, request);
  }

  const records: CloseTimesDisplayRecord[] = closingTimes.map((closingTime) => {
    const request = requestByClosingTimeIri.get(closingTime["@id"]);
    if (!request) {
      return {
        kind: "closing-time",
        key: `closing-time-${closingTime.id}`,
        closingTime,
        startDate: closingTime.startDate,
        endDate: closingTime.endDate,
        editedBy: closingTime.editedBy ?? null,
        schoolClass: closingTime.schoolClass ?? null,
        name: closingTime.name,
      };
    }

    return {
      ...toRequestFields(request),
      kind: "pending-change-request",
      request,
      closingTime,
      startDate: request.startDate ?? closingTime.startDate,
      endDate: request.endDate ?? closingTime.endDate,
      editedBy: request.editedBy ?? closingTime.editedBy ?? null,
      schoolClass: request.schoolClass ?? closingTime.schoolClass ?? null,
      name: request.name ?? closingTime.name,
    };
  });

  for (const request of createRequests) {
    records.push({
      ...toRequestFields(request),
      kind: "pending-create-request",
      request,
    });
  }

  return {
    records: sortCloseTimesDisplayRecords(records),
    anomalies,
  };
};
